// Seller identity chips of the seller dialog, as pure helpers. The API sends one view per seller
// row: per other marketplace its linked member (the status is the pair's) and the suggested
// candidates, best first.

const STATE_SEVERITY = {confirmed: 'success', auto: 'info', suggested: 'warning'};

export const officialLabel = (official, t) => {
    if (official?.kind === 'exact') return t('sellers_workspace.official.exact', 'Resmi kayıt eşleşti');
    if (official?.kind === 'different' && official?.field === 'name') return t('sellers_workspace.official.sameName', 'Aynı isim, resmi kayıt farklı');
    if (official?.kind === 'different') return t('sellers_workspace.official.different', 'Resmi kayıt farklı');
    return '';
};

// A difference only warns: the status beside it stays what it was, and a person decides.
export const chipSeverity = (chip) => chip.evidence?.official?.kind === 'different' ? 'danger' : STATE_SEVERITY[chip.state];

export const sellerChips = (view, marketplaces, notFoundOn) => {
    if (!view) return [];
    return (marketplaces || [])
        .filter(marketplace => marketplace.key !== view.marketplace_key)
        .map(marketplace => {
            const link = view.links?.[marketplace.key] || {member: null, candidates: []};
            const candidates = link.candidates || [];
            const base = {key: marketplace.key, label: marketplace.label, link};
            if (link.member) {
                return {...base, state: link.member.status, name: link.member.seller_name, extraCandidates: candidates.length,
                        evidence: link.member.evidence, strength: link.member.strength, via: link.member.via};
            }
            if (candidates.length) {
                return {...base, state: 'suggested', name: candidates[0].seller_name, extraCandidates: candidates.length - 1,
                        evidence: candidates[0].evidence, strength: candidates[0].strength, via: null};
            }
            return {...base, state: 'empty', name: null, extraCandidates: 0, evidence: null, via: null,
                    notFound: (notFoundOn || []).includes(marketplace.key)};
        });
};

export const strengthLabel = (strength, t) => ({
    strong: t('sellers_workspace.strength.strong', 'güçlü'),
    likely: t('sellers_workspace.strength.likely', 'olası'),
    weak: t('sellers_workspace.strength.weak', 'zayıf'),
}[strength] || '');

// A chip always says what kind of match it is: a link nobody reviewed keeps how strong its evidence was,
// unless an official record already proved or contradicted it.
export const chipText = (chip, t) => {
    if (chip.state === 'empty') return `${chip.label}: ${chip.notFound ? t('sellers_workspace.notFound', 'bulunamadı') : '—'}`;
    const extra = chip.extraCandidates > 0 ? ` +${chip.extraCandidates}` : '';
    const official = officialLabel(chip.evidence?.official, t);
    const label = official ? ` · ${official}` : '';
    const strength = !official && strengthLabel(chip.strength, t) ? ` · ${strengthLabel(chip.strength, t)}` : '';
    if (chip.state === 'confirmed') return `${chip.label}: ${chip.name} ✓${label}`;
    if (chip.state === 'auto') return `${chip.label}: ${chip.name} (${t('onboarding.sites.identity.automatic', 'otomatik')}${strength})${extra}${label}`;
    return `${chip.label}: ${chip.name} (${t('onboarding.sites.identity.suggestion', 'öneri')}${strength})${extra}${label}`;
};

export const evidenceText = (evidence, t) => {
    if (!evidence) return '';
    const parts = [];
    const official = officialLabel(evidence.official, t);
    if (official) parts.push(official);
    if (evidence.name_equal) parts.push(t('onboarding.sites.identity.sameName', 'Aynı isim'));
    if (evidence.shared_products !== undefined) {
        parts.push(t('onboarding.sites.identity.sharedProducts', '{{count}} ortak ürün (%{{percent}})',
            {count: evidence.shared_products, percent: Math.round((evidence.overlap_ratio || 0) * 100)}));
    }
    if (evidence.seed) {
        parts.push(evidence.seed_verified
            ? t('onboarding.sites.identity.fromSeedVerified', 'Ön çalışma: resmi kayıt eşleşti')
            : t('onboarding.sites.identity.fromSeed', 'Ön çalışmadan, doğrulanmadı'));
    }
    return parts.join(' · ');
};

export const chipTooltip = (chip, t) => {
    const parts = [];
    const evidence = evidenceText(chip.evidence, t);
    if (evidence) parts.push(evidence);
    if (chip.via) parts.push(t('onboarding.sites.identity.via', '{{seller}} üzerinden bağlı', {seller: chip.via.seller_name}));
    return parts.join(' · ');
};

const links = (view) => Object.values(view?.links || {});

export const matchesIdentityFilter = (view, filter) => {
    if (filter === 'all') return true;
    if (filter === 'unmatched') return links(view).every(link => !link.member && !(link.candidates || []).length);
    if (filter === 'suggestions') return links(view).some(link => !link.member && (link.candidates || []).length > 0);
    if (filter === 'auto') return links(view).some(link => link.member?.status === 'auto');
    return true;
};
